import { createContext, useCallback, useContext, useEffect, useState } from "react";
import axios, { API } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/auth/me`);
      setUser(res.data);
    } catch (err) {
      // 401 from /me is the explicit "not signed in" signal — not an error.
      // Anything else is logged for dev visibility but doesn't surface to UX.
      if (err?.response?.status !== 401) {
        // eslint-disable-next-line no-console
        console.debug("[auth] /me check failed:", err?.message);
      }
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const logout = useCallback(async () => {
    try {
      await axios.post(`${API}/auth/logout`);
    } catch (err) {
      // Best-effort — if the server is unreachable we still clear local state.
      // eslint-disable-next-line no-console
      console.debug("[auth] logout server call failed:", err?.message);
    }
    setUser(null);
  }, []);

  return (
    <AuthContext.Provider value={{ user, loading, setUser, refresh: checkAuth, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
